import React, { useCallback, useContext, useState } from 'react';
import type { GridApi } from 'ag-grid-community';

import CanvasCard from '../../Visualisations/CanvasCard';
import CanvasCardTitle from '../../CanvasCardTitle';
import ContextMenu, { Option } from 'kingpin/atoms/ContextMenu';
import CopyDashboardGadgetToReportButton from '../../CopyDashboardGadgetToReportButton';
import CopyGadgetButton from '../../CopyGadgetButton';
import CopyReportGadgetButton from '../../CopyReportGadgetButton/CopyReportGadgetButton';
import CopyReportGadgetToDashboardButton from '../../CopyReportGadgetToDashboardButton';
import DashboardGadgetBottomBar from '../../DashboardGadgetBottomBar';
import DashboardGadgetContext from 'contexts/DashboardGadgetContext';
import DashboardGadgetFilteringButton from '../../DashboardGadgetFilteringButton';
import DashboardGadgetReportLinkButton from '../../DashboardGadgetReportLinkButton';
import ExportButton from '../../Grid/ExportButton';
import GridCsvExportRegistrar from '../../Grid/ExportButton/GridCsvExportRegistrar';
import FleetOpsStaffOnly from '../../Common/FleetOpsStaffOnly';
import GadgetFullscreenButton from '../../GadgetFullscreen/GadgetFullscreenButton';
import { useGadgetFullscreen } from '../../GadgetFullscreen/GadgetFullscreenProvider';
import MatrixContent from './MatrixContent';
import MetricsInfoButton from '../../MetricsInfoButton';
import ReportGadgetEditOption from '../../ReportGadgetEditOption';
import ReportGadgetContext from '../../../contexts/ReportGadgetContext';
import Row from '../../Common/Row';
import ShowOnMouseOverUnlessTour from '../../ShowOnMouseOverUnlessTour';
import buildShowAllKey from '../buildShowAllKey';
import isSingleMetricDateMatrix from '../../../types/visTypeCheckers/isSingleMetricDateMatrix';
import isV5ChartDef from '../../../types/visTypeCheckers/isV5ChartDef';
import useReportAnIssueModal from '../../ReportAnIssue/useReportAnIssueModal';
import {
  usePublishBlockExportHandle,
  usePublishBlockGridApi,
} from '../../Studio/BlockGridApiContext';
import { MatrixSortProvider } from './MatrixSortContext';

const getIssueReportType = (
  chartDefinition: VisualisationDefinition | undefined,
): GadgetType | undefined => {
  if (!chartDefinition || !isV5ChartDef(chartDefinition)) {
    return undefined;
  }
  return chartDefinition.gadgetType;
};

export const TopBar = ({
  gridApi,
  exportHandle,
  exportName,
  chartDefinition,
  title,
}: {
  gridApi?: GridApi;
  // DataTable's stand-in for gridApi when drawn by the new table
  exportHandle?: DataTable.ExportHandle;
  exportName?: string;
  chartDefinition?: VisualisationDefinition;
  title: string;
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const onMenuOpen = useCallback(() => {
    setIsMenuOpen(true);
  }, []);
  const onMenuClose = useCallback(() => {
    setIsMenuOpen(false);
  }, []);
  const { dashboardGadget } = useContext(DashboardGadgetContext);
  const reportGadgetContext = useContext(ReportGadgetContext);
  const { isFullscreen } = useGadgetFullscreen();

  const issueReportType = getIssueReportType(chartDefinition);
  const { open: openReportAnIssue, modal: reportAnIssueModal } =
    useReportAnIssueModal({
      type: issueReportType || 'dashboard-gadget',
      gadgetName: chartDefinition?.name,
    });
  return (
    <Row
      spaceBetween
      style={{
        zIndex: 'unset',
        width: '100%',
        height: '100%',
      }}
      centerAlign
    >
      {/* Registers this matrix's CSV export during scheduled-report PDF export.
          The ExportButton below only mounts when the ContextMenu is opened, so
          it never registers during a headless render — this covers that gap.
          No-op unless isPdfExport (see GridCsvExportRegistrar). */}
      {(gridApi || exportHandle) && exportName && (
        <GridCsvExportRegistrar
          overrideGridApi={gridApi}
          exportHandle={exportHandle}
          exportName={exportName}
        />
      )}
      <CanvasCardTitle title={title} />
      <Row centerAlign>
        <ShowOnMouseOverUnlessTour>
          <div
            style={{ marginRight: 8 }}
            id={
              chartDefinition && isSingleMetricDateMatrix(chartDefinition)
                ? buildShowAllKey({
                    dashboardGadget,
                    chartDef: chartDefinition,
                    isFullscreen,
                  })
                : undefined
            }
          />
        </ShowOnMouseOverUnlessTour>
        {!isFullscreen && (
          <ShowOnMouseOverUnlessTour>
            <DashboardGadgetReportLinkButton />
          </ShowOnMouseOverUnlessTour>
        )}
        <GadgetFullscreenButton />
        {!!chartDefinition && !isFullscreen && (
          <ShowOnMouseOverUnlessTour>
            <div style={{ marginRight: 8 }}>
              <MetricsInfoButton visualisation={chartDefinition} />
            </div>
          </ShowOnMouseOverUnlessTour>
        )}
        {!isFullscreen && (
          <ShowOnMouseOverUnlessTour isForcedVisible={isMenuOpen}>
            <ContextMenu
              buttonType="Ghost"
              onClose={onMenuClose}
              onOpen={onMenuOpen}
              testId="report-gadget-card-menu"
            >
              <>
                <ReportGadgetEditOption />
                <DashboardGadgetFilteringButton />
                {(gridApi !== undefined || exportHandle !== undefined) &&
                  exportName !== undefined && (
                    <ExportButton
                      overrideGridApi={gridApi}
                      exportHandle={exportHandle}
                      exportName={exportName}
                      isContextMenuItem
                      isTotalsRowIncluded
                    />
                  )}
                {reportGadgetContext?.reportGadget ? (
                  <>
                    <CopyReportGadgetToDashboardButton
                      reportGadget={reportGadgetContext.reportGadget}
                    />
                    <CopyReportGadgetButton
                      reportGadget={reportGadgetContext.reportGadget}
                    />
                  </>
                ) : (
                  <>
                    <CopyGadgetButton label="Copy to Dashboard" />
                    <CopyDashboardGadgetToReportButton
                      chartDef={chartDefinition}
                    />
                  </>
                )}
                {issueReportType && (
                  <FleetOpsStaffOnly>
                    <Option
                      label="Report an issue"
                      onClick={openReportAnIssue}
                      testId="report-an-issue"
                    />
                  </FleetOpsStaffOnly>
                )}
              </>
            </ContextMenu>
          </ShowOnMouseOverUnlessTour>
        )}
        {reportAnIssueModal}
      </Row>
    </Row>
  );
};

const MatrixContainer = ({
  chartDefinition,
}: {
  chartDefinition: V5ChartDefinition;
}) => {
  const { dashboardGadget } = useContext(DashboardGadgetContext);
  const [gridApi, setGridApi] = useState<GridApi>();
  const [exportHandle, setExportHandle] = useState<DataTable.ExportHandle>();
  // A Studio totals Table exports it from its own menu (its header, with
  // the Export above, is hidden there)
  usePublishBlockGridApi(gridApi);
  usePublishBlockExportHandle(exportHandle);

  return (
    <CanvasCard
      topBar={
        <TopBar
          title={chartDefinition.name}
          gridApi={gridApi}
          exportHandle={exportHandle}
          exportName={chartDefinition.name}
          chartDefinition={chartDefinition}
        />
      }
      content={
        <MatrixSortProvider chartDefinition={chartDefinition}>
          <MatrixContent
            // Remount when the Total column position toggles. ag-grid preserves
            // the existing order/pinned-state of already-rendered columns when
            // `columnDefs` is updated in place (same colIds), so it ignores the
            // reordered/re-pinned Total column otherwise. Keyed only on this
            // rare, explicit setting so normal data refetches don't remount.
            key={`totals-${chartDefinition.totalsColumnPosition ?? 'last'}`}
            chartDefinition={chartDefinition}
            gridApi={gridApi}
            setGridApi={setGridApi}
            setExportHandle={setExportHandle}
          />
        </MatrixSortProvider>
      }
      bottomBar={dashboardGadget ? <DashboardGadgetBottomBar /> : undefined}
      isContentWithoutPadding
    />
  );
};

export default MatrixContainer;
